import { defineStore } from "pinia";

import * as draftStorage from "@/components/session/sessionDraftStorage";
import { intercomApi, intercomId, isCloud } from "@/environment";
import { TeamHandler } from "@/handlers/teamHandler";

const noEntities = { team: [], art: [], train: [] };
const noPermissions = {
  read: noEntities,
  edit: noEntities,
  session: noEntities,
};

function hasAccess(user, entity, permission) {
  return (objOrId) => {
    const perms = user.permissions[permission]?.[entity];
    if (!perms) return false;
    return perms[0] < 0 || perms.includes(objOrId?.id || objOrId);
  };
}

export const useAppStore = defineStore("app", {
  state: () => ({
    crossNotAvailable: false,
    onPrem: false,
    wsconnection: {},
    natsconnection: null,
    natsState: "disconnected",
    natsReconnects: 0,
    natsScheme: "v1",
    user: {},
    authServerErr: "",
    startedPageState: "default",
    stateManager: [],
    isSSOEnabled: false,
    order: {},
    license: {},
    licenseUsedCount: null,
    shouldOnboard: false,
  }),

  getters: {
    company: (state) => state.user.company,
    isAdmin: (state) => state.user.role === "admin",
    isPlanningIntervalAdmin: (state) => state.user.role === "planning_interval_admin",
    isObserver: (state) => state.user.role === "observer",
    isTraining() {
      return this.company && this.company.startsWith("pips-");
    },
    session: (state) => () => {
      if (!state.wsconnection.session?.isOpen) {
        throw new Error("Autobahn session is not open");
      }
      return state.wsconnection.session;
    },
    isRetryingConnection: (state) => state.wsconnection?.isRetrying,
    canEditTeam: (state) => hasAccess(state.user, "team", "edit"),
    canEditArt: (state) => hasAccess(state.user, "art", "edit"),
    canEditTrain: (state) => hasAccess(state.user, "train", "edit"),
    canEditSessionWithArt: (state) => hasAccess(state.user, "art", "session"),
    canEditSessionWithTrain: (state) => hasAccess(state.user, "train", "session"),
    intercomAvailable: (state) =>
      isCloud && !!intercomId && !!intercomApi && state.license?.tracking === true,
  },

  actions: {
    setLicense(license) {
      this.license = license;
    },
    setShouldOnboard(shouldOnboard) {
      this.shouldOnboard = shouldOnboard;
    },
    async fetchLicenseUsage() {
      try {
        const { data: teams } = await new TeamHandler().getTeams();
        this.licenseUsedCount = (teams || []).filter(
          (team) => !team.archived && team.expiryDate,
        ).length;
      } catch {
        this.licenseUsedCount = null;
      }
    },
    notAvailable() {
      this.crossNotAvailable = true;
    },
    available() {
      this.crossNotAvailable = false;
    },
    setOnPrem(onPrem) {
      this.onPrem = onPrem;
    },
    enableSSO() {
      this.isSSOEnabled = true;
    },
    disableSSO() {
      this.isSSOEnabled = false;
    },
    setUserInfo(info) {
      const [company, id] = info.authid.split(".");
      this.user = {
        company,
        id,
        role: info.authrole,
        permissions: noPermissions,
      };
    },
    setUser(user) {
      this.user = user;
    },
    setConnection(connection) {
      this.wsconnection = connection;
    },
    setNatsConnection(connection) {
      this.natsconnection = connection;
    },
    setNatsScheme(scheme) {
      this.natsScheme = scheme;
    },
    connectWebsocket() {
      this.natsState = "connected";
    },
    disconnectWebsocket() {
      this.natsState = "disconnected";
    },
    reconnectWebsocket() {
      this.natsState = "connected";
      this.natsReconnects++;
    },
    closeConnection() {
      this.wsconnection.close("wamp.close.logout");
      this.license = {};
    },
    setServerError(err) {
      this.authServerErr = err;
    },
    setStartedPageState(newState) {
      this.stateManager.push(this.startedPageState);
      draftStorage.setStateStack(this.stateManager);
      this.startedPageState = newState;
      this._scrollTop();
      draftStorage.setCurrentState(newState);
    },
    setState(newState) {
      this.startedPageState = newState;
      this._scrollTop();
      draftStorage.setCurrentState(newState);
    },
    restoreStateStack(stateStack) {
      this.stateManager = stateStack;
    },
    getPreviousState() {
      this.startedPageState = this.stateManager.pop();
      draftStorage.setStateStack(this.stateManager);
    },
    clearStateManager() {
      this.stateManager = [];
      this.startedPageState = "default";
      draftStorage.clearState();
    },
    setOrder({ company, price, numberOfLicenses, validityStartDate, validityEndDate }) {
      this.order = {
        company,
        price,
        numberOfLicenses,
        validityStartDate,
        validityEndDate,
      };
    },
    _scrollTop() {
      document.querySelector(".layout__content")?.scrollTo({ top: 0, behavior: "smooth" });
    },
  },
});
